/**
 * DCF resubmittals as a Kanban (Isaiah Cotto, 2026-10-15: "Reversals are really resubmittals. Lets move it to a kanban in
 * finance"): the operational kit (components/board/kanban) over GET /finance/resubmittals
 * (services/finance/resubmittalBoard.js). One card is ONE DCF RESUBMITTAL.
 *
 * The server places every card in a column and counts it; "Show more" reads that column's next page. A drop never
 * writes (RESUBMITTAL_TRANSITIONS): an allowed move opens its form in the docked panel and records nothing until the
 * person confirms there; anything else is refused in one line. UMED records the sending and DCF's answer, so no drop
 * claims them. Without can.manage the board is read-only and says so in one line.
 *
 * The docked panel is the card's record and its acts (hand to UMED, withdraw, state the correction, add a reason) and,
 * as "New DCF resubmittal", the start form. Never a dropdown inside a column. After any act the board is read again.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { OperationalKanban, CardChips } from '../../components/board/kanban';
import type { KanbanColumnData } from '../../components/board/kanban';
import { useBoardUrlState } from '../../components/board';
import { outcomeOf, shortDate } from './financeApi';
import type { ApiResult } from './financeApi';
import { easternDay } from './HandoffWork';
import { resubmittalsApi } from './resubmittalsApi';
import type {
  ResubmittalBoardPage, ResubmittalCardRow, ResubmittalCandidate, ResubmittalKind, ResubmittalRequester, CorrectedTerms, ResubmittalStartStatus,
} from './resubmittalsApi';
import {
  RESUBMITTAL_TRANSITIONS, READ_ONLY_WHY, KIND_LABEL, KIND_NOT_STATED, REQUESTER_LABEL, EMPTY_TERMS,
  cardFromRow, chipsFor, dollars, termsBody, termsLine, truncate, actsOn, handAvailable,
} from './resubmittalsModel';
import type { ResubmittalCard, ResubmittalCtx, ResubmittalAct, TermsFields } from './resubmittalsModel';

/** Cards per column per page (the server caps it at 100). */
export const RESUBMITTALS_PAGE_SIZE = 25;
const NEW_ID = 'new';
const REASON_MIN = 3;
const REASON_MAX = 2000;
const REQUESTERS: ResubmittalRequester[] = ['FACILITY', 'RESPONSIBLE_PARTY', 'DCF', 'SFS', 'OTHER'];

const ACT_WORD: Record<ResubmittalAct, string> = {
  'hand-to-umed': 'Hand to UMED', withdraw: 'Withdraw', correction: 'State the correction', reason: 'Add a reason',
};

const day = (ts: string | null | undefined) => (ts ? easternDay(ts) : null);
const None = ({ children = 'not recorded' }: { children?: React.ReactNode }) => <span className="fn-kc-none">{children}</span>;

/** What a resubmittal card says. Phrasing content only: the card's body is the button that opens it. */
export function ResubmittalCardBody({ card }: { card: ResubmittalCard }) {
  const total = card.kind === 'CORRECTED' ? dollars(card.corrected_terms ? card.corrected_terms.total_cents : null) : null;
  const requested = day(card.requested_at);
  const first = truncate(card.reason);
  return (
    <>
      <span className="okb-c-name">{card.name || 'Name not recorded'}</span>
      <span className="okb-c-sub" title={card.facility_name || undefined}>{card.facility_name || 'No current building'}</span>
      <CardChips chips={chipsFor(card)} />
      {card.kind === 'CORRECTED' && (
        <span className="okb-c-row fn-kc-line">
          <span className="okb-c-next">Corrected total</span>{' '}
          <span className="okb-c-due">{total || <None>not stated</None>}</span>
        </span>
      )}
      <span className="okb-c-row fn-kc-line">
        <span className="okb-c-next">Requested by</span>{' '}
        <span className="okb-c-due">{REQUESTER_LABEL[card.requester] || card.requester}</span>
      </span>
      <span className="okb-c-row"><span className="okb-c-next" title={card.reason || undefined}>{first || <None>No reason recorded</None>}</span></span>
      <span className="okb-c-foot"><span className="okb-c-owner">{requested ? `Requested ${requested}` : 'Request date not recorded'}</span></span>
    </>
  );
}

/* ── Act plumbing ─────────────────────────────────────────────────────────────────────────────────────────────── */

/** One write at a time; a refusal is said verbatim and nothing else changes. */
function useWrite() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<ApiResult<unknown>>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true); setErr(null);
    try {
      const r = await fn();
      if (r.success) return true;
      setErr(r.status === undefined && !r.message && !r.error ? 'Could not reach the server; it may or may not have been recorded. Read the board again before trying twice.' : (r.message || r.error || outcomeOf(r, { wrote: true })));
      return false;
    } catch {
      setErr('Could not reach the server; it may or may not have been recorded. Read the board again before trying twice.');
      return false;
    } finally { setBusy(false); }
  };
  return { busy, err, setErr, run };
}

const Alert = ({ children }: { children: React.ReactNode }) => <p className="um-notice stop" role="alert">{children}</p>;

/** The corrected terms' inputs. Exported for UMED's own start (BoardTable, 2026-10-19): one form for the one `corrected_terms`. */
/*
 * Also the original terms' inputs on a UMED case (2026-10-21): the same boxes with their own legend and total label,
 * the first and last month required, and no free-text line (the server composes it from the figures).
 */
export function TermsInputs({ idp, value, onChange, legend = 'Corrected terms, as DCF will be sent them', totalLabel = 'Corrected total ($)', datesRequired = false, withText = true }: {
  idp: string; value: TermsFields; onChange: (v: TermsFields) => void; legend?: string; totalLabel?: string; datesRequired?: boolean; withText?: boolean;
}) {
  const set = (k: keyof TermsFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...value, [k]: e.target.value });
  const row: React.CSSProperties = { display: 'grid', gap: 4, marginBottom: 10 };
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="um-quiet" style={{ marginBottom: 6 }}>{legend}</legend>
      <div style={row}><label htmlFor={`${idp}-months`}>Months (1 to 60)</label>
        <input id={`${idp}-months`} inputMode="numeric" value={value.months} onChange={set('months')} /></div>
      <div style={row}><label htmlFor={`${idp}-monthly`}>Monthly amount ($)</label>
        <input id={`${idp}-monthly`} inputMode="decimal" value={value.monthly} onChange={set('monthly')} /></div>
      <div style={row}><label htmlFor={`${idp}-total`}>{totalLabel}</label>
        <input id={`${idp}-total`} inputMode="decimal" value={value.total} onChange={set('total')} /></div>
      <div style={row}><label htmlFor={`${idp}-starts`}>{datesRequired ? 'First month' : 'Starts on (optional)'}</label>
        <input id={`${idp}-starts`} type="date" value={value.startsOn} onChange={set('startsOn')} required={datesRequired} /></div>
      <div style={row}><label htmlFor={`${idp}-ends`}>{datesRequired ? 'Last month' : 'Ends on (optional)'}</label>
        <input id={`${idp}-ends`} type="date" value={value.endsOn} onChange={set('endsOn')} required={datesRequired} /></div>
      {withText && (
        <div style={row}><label htmlFor={`${idp}-text`}>Terms in words (optional)</label>
          <textarea id={`${idp}-text`} rows={2} value={value.text} onChange={set('text')} /></div>
      )}
    </fieldset>
  );
}

function KindChoice({ idp, value, onChange }: { idp: string; value: ResubmittalKind | ''; onChange: (k: ResubmittalKind) => void }) {
  return (
    <fieldset style={{ border: 0, padding: 0, margin: '0 0 10px' }}>
      <legend>What is being resubmitted</legend>
      {(['CORRECTED', 'FULL'] as ResubmittalKind[]).map((k) => (
        <label key={k} htmlFor={`${idp}-kind-${k}`} style={{ display: 'block', margin: '4px 0' }}>
          <input id={`${idp}-kind-${k}`} type="radio" name={`${idp}-kind`} checked={value === k} onChange={() => onChange(k)} />{' '}
          {KIND_LABEL[k]}{k === 'CORRECTED' ? ': the claim stands at corrected terms' : ': the claim is unwound'}
        </label>
      ))}
    </fieldset>
  );
}

const fieldsOf = (t: CorrectedTerms | null | undefined): TermsFields => (t ? {
  months: String(t.months), monthly: (t.monthly_cents / 100).toFixed(2), total: (t.total_cents / 100).toFixed(2),
  startsOn: t.starts_on || '', endsOn: t.ends_on || '', text: '',
} : EMPTY_TERMS);

type Done = (message: string, cardId?: string) => void;

function HandForm({ card, onDone, onCancel }: { card: ResubmittalCard; onDone: Done; onCancel: () => void }) {
  const w = useWrite();
  const terms = termsLine(card.corrected_terms);
  const go = async () => { if (await w.run(() => resubmittalsApi.handToUmed(card.id))) onDone(`Handed to UMED: ${card.name || 'this resubmittal'} is now With UMED.`, card.id); };
  return (
    <form aria-label="Hand to UMED" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <p>Hand this DCF resubmittal to UMED, which prepares the packet and faxes it to DCF. Finance can still withdraw it until it is sent. Nothing is recorded until you confirm.</p>
      <dl className="um-quiet" style={{ margin: '0 0 10px' }}>
        <dt>Kind</dt><dd>{card.kind ? KIND_LABEL[card.kind] : KIND_NOT_STATED}</dd>
        {card.kind === 'CORRECTED' && (<><dt>Corrected terms</dt><dd>{terms || 'not stated'}</dd><dt>Corrected total</dt><dd>{dollars(card.corrected_terms ? card.corrected_terms.total_cents : null) || 'not stated'}</dd></>)}
        {card.kind === 'FULL' && (<><dt>Corrected terms</dt><dd>None: the whole claim is unwound.</dd></>)}
      </dl>
      {w.err && <Alert>{w.err}</Alert>}
      <button type="submit" className="um-open um-primary" disabled={w.busy}>{w.busy ? 'Handing over…' : 'Hand to UMED'}</button>{' '}
      <button type="button" className="um-open" onClick={onCancel}>Cancel</button>
    </form>
  );
}

function ReasonField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'grid', gap: 4, marginBottom: 10 }}>
      <label htmlFor={id}>{label}</label>
      <textarea id={id} rows={3} value={value} maxLength={REASON_MAX} onChange={(e) => onChange(e.target.value)} aria-describedby={`${id}-internal`} />
      <span id={`${id}-internal`} className="why">Internal to eMerge. Never printed on the DCF fax cover.</span>
    </div>
  );
}
const reasonProblem = (s: string) => { const n = s.trim().length; return n < REASON_MIN || n > REASON_MAX ? `A reason is ${REASON_MIN} to ${REASON_MAX} characters.` : null; };

function WithdrawForm({ card, onDone, onCancel }: { card: ResubmittalCard; onDone: Done; onCancel: () => void }) {
  const w = useWrite();
  const [reason, setReason] = useState('');
  const [bad, setBad] = useState<string | null>(null);
  const go = async () => {
    const p = reasonProblem(reason);
    if (p) { setBad(`Withdrawing needs its reason. ${p}`); return; }
    setBad(null);
    if (await w.run(() => resubmittalsApi.withdraw(card.id, reason.trim()))) onDone(`Withdrawn: ${card.name || 'this resubmittal'}.`, card.id);
  };
  return (
    <form aria-label="Withdraw" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <p>Withdraw this DCF resubmittal before it is sent. Nothing is recorded until you confirm.</p>
      <ReasonField id={`rs-withdraw-${card.id}`} label="Reason for withdrawing" value={reason} onChange={setReason} />
      {(bad || w.err) && <Alert>{bad || w.err}</Alert>}
      <button type="submit" className="um-open um-primary" disabled={w.busy}>{w.busy ? 'Withdrawing…' : 'Withdraw'}</button>{' '}
      <button type="button" className="um-open" onClick={onCancel}>Cancel</button>
    </form>
  );
}

function CorrectionForm({ card, onDone, onCancel }: { card: ResubmittalCard; onDone: Done; onCancel: () => void }) {
  const w = useWrite();
  const [kind, setKind] = useState<ResubmittalKind | ''>(card.kind || '');
  const [terms, setTerms] = useState<TermsFields>(fieldsOf(card.corrected_terms));
  const [bad, setBad] = useState<string | null>(null);
  const go = async () => {
    if (!kind) { setBad('Choose whether this is a corrected or a full resubmittal.'); return; }
    let body: { kind: ResubmittalKind; corrected_terms?: CorrectedTerms } = { kind };
    if (kind === 'CORRECTED') {
      const t = termsBody(terms);
      if (!t.ok) { setBad(t.message); return; }
      body = { kind, corrected_terms: t.terms };
    }
    setBad(null);
    if (await w.run(() => resubmittalsApi.correction(card.id, body))) onDone(`Recorded: ${KIND_LABEL[kind]}${kind === 'CORRECTED' ? ' with its corrected terms' : ''}.`, card.id);
  };
  return (
    <form aria-label="State the correction" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <p>State what is being resubmitted. Nothing is recorded until you save.</p>
      <KindChoice idp={`rs-corr-${card.id}`} value={kind} onChange={setKind} />
      {kind === 'CORRECTED' && <TermsInputs idp={`rs-corr-${card.id}`} value={terms} onChange={setTerms} />}
      {(bad || w.err) && <Alert>{bad || w.err}</Alert>}
      <button type="submit" className="um-open um-primary" disabled={w.busy}>{w.busy ? 'Saving…' : 'Save the correction'}</button>{' '}
      <button type="button" className="um-open" onClick={onCancel}>Cancel</button>
    </form>
  );
}

function ReasonForm({ card, onDone, onCancel }: { card: ResubmittalCard; onDone: Done; onCancel: () => void }) {
  const w = useWrite();
  const [reason, setReason] = useState('');
  const [bad, setBad] = useState<string | null>(null);
  const go = async () => {
    const p = reasonProblem(reason);
    if (p) { setBad(p); return; }
    setBad(null);
    if (await w.run(() => resubmittalsApi.addReason(card.id, reason.trim()))) onDone('Reason added.', card.id);
  };
  return (
    <form aria-label="Add a reason" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <ReasonField id={`rs-reason-${card.id}`} label="Another reason for this resubmittal" value={reason} onChange={setReason} />
      {(bad || w.err) && <Alert>{bad || w.err}</Alert>}
      <button type="submit" className="um-open um-primary" disabled={w.busy}>{w.busy ? 'Saving…' : 'Add the reason'}</button>{' '}
      <button type="button" className="um-open" onClick={onCancel}>Cancel</button>
    </form>
  );
}

/* ── The docked record ────────────────────────────────────────────────────────────────────────────────────────── */

export interface ResubmittalRequest { cardId: string; act: ResubmittalAct; n: number }

export function Facts({ card }: { card: ResubmittalCard }) {
  const terms = termsLine(card.corrected_terms);
  const prior = card.prior_terms;
  const approved = card.approved_on ? shortDate(card.approved_on) : null;
  return (
    <dl className="um-quiet" style={{ margin: '0 0 12px' }}>
      <dt>Kind</dt><dd>{card.kind ? KIND_LABEL[card.kind] : KIND_NOT_STATED}</dd>
      {card.kind === 'CORRECTED' && (<><dt>Corrected terms</dt><dd>{terms || 'not stated'}</dd><dt>Corrected total</dt><dd>{dollars(card.corrected_terms ? card.corrected_terms.total_cents : null) || 'not stated'}</dd></>)}
      {prior && prior.terms && (
        <><dt>Replaces</dt><dd>Previously submitted: {prior.terms}{prior.total !== null ? ` · ${dollars(prior.total * 100)}` : ''}{prior.date ? ` · ${shortDate(prior.date)}` : ''}</dd></>
      )}
      <dt>Requested by</dt><dd>{REQUESTER_LABEL[card.requester] || card.requester}{card.requested_by_name ? `, recorded by ${card.requested_by_name}` : ''}{card.requested_at ? ` · ${day(card.requested_at)}` : ''}</dd>
      <dt>{card.reasons_count > 1 ? `Reasons (${card.reasons_count}; the first is shown)` : 'Reason'}</dt><dd>{card.reason || 'No reason recorded'}</dd>
      {approved && (<><dt>Approved on</dt><dd>{approved}</dd></>)}
      {card.handed_to_umed_at && (<><dt>Handed to UMED</dt><dd>{day(card.handed_to_umed_at)}{card.handed_by_name ? ` by ${card.handed_by_name}` : ''}</dd></>)}
      {card.packet && (<><dt>Packet</dt><dd>{card.packet.state ? String(card.packet.state).toLowerCase().replace(/_/g, ' ') : 'state not recorded'}{card.packet.blockers && card.packet.blockers.length ? ` · blocked: ${card.packet.blockers.join(', ')}` : ''}</dd></>)}
      {card.submission_count > 0 && (<><dt>Sent to DCF</dt><dd>{card.submission_count} {card.submission_count === 1 ? 'time' : 'times'}{card.last_submitted_at ? `, last ${day(card.last_submitted_at)}` : ''}</dd></>)}
      {card.outcome && (<><dt>DCF's answer</dt><dd>{String(card.outcome).toLowerCase().replace(/_/g, ' ')}{card.outcome_recorded_at ? ` · ${day(card.outcome_recorded_at)}` : ''}</dd></>)}
      {card.state === 'APPROVED' && card.finance_handoff_words && (<><dt>Finance handoff</dt><dd>{card.finance_handoff_words}</dd></>)}
      {card.withdrawn_at && (<><dt>Withdrawn</dt><dd>{day(card.withdrawn_at)}{card.withdrawn_by_name ? ` by ${card.withdrawn_by_name}` : ''}{card.withdrawn_reason ? `: ${card.withdrawn_reason}` : ''}</dd></>)}
    </dl>
  );
}

function CardPanel({ id, card: given, ctx, request, onConsumed, onChanged }: {
  id: string; card: ResubmittalCard | null; ctx: ResubmittalCtx; request: ResubmittalRequest | null;
  onConsumed: (n: number) => void; onChanged: (message: string, from?: string | null) => void;
}) {
  const [fresh, setFresh] = useState<ResubmittalCard | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const [form, setForm] = useState<ResubmittalAct | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const saidRef = useRef<HTMLParagraphElement>(null);
  const card = fresh && fresh.id === id ? fresh : given;

  // A card the loaded pages do not hold (a deep link, or an act moved it out of view): read it by its id.
  useEffect(() => {
    if (given) { setFresh(null); return undefined; }
    let live = true;
    (async () => {
      try {
        const r = await resubmittalsApi.card(id);
        if (!live) return;
        if (r.success && r.data) { setFresh(cardFromRow(r.data)); setMissing(null); } else setMissing(r.code === 'NOT_FOUND' ? 'This DCF resubmittal is not here. It may be outside your buildings.' : outcomeOf(r));
      } catch { if (live) setMissing('Could not reach the server.'); }
    })();
    return () => { live = false; };
  }, [id, given]);

  // A drop's request: made for this card, taken once.
  useEffect(() => {
    if (request && request.cardId === id) { setForm(request.act); setSaid(null); onConsumed(request.n); }
  }, [request, id, onConsumed]);

  useEffect(() => { if (said && saidRef.current) saidRef.current.focus(); }, [said]);

  if (!card) return <section className="um-card"><p className="um-quiet" role={missing ? 'alert' : 'status'}>{missing || 'Reading this DCF resubmittal…'}</p></section>;

  const done: Done = (message) => {
    setForm(null); setSaid(message);
    onChanged(message, id); // said here (focused); the board's line shows it without announcing it again
    // The card as it stands now, so the panel never shows what the act just changed as it was.
    resubmittalsApi.card(card.id).then((r) => { if (r.success && r.data) setFresh(cardFromRow(r.data)); }).catch(() => undefined);
  };
  const acts = actsOn(card, ctx);
  const cancel = () => setForm(null);
  return (
    <section className="um-card" aria-label="This DCF resubmittal">
      <div className="um-card-head"><div>
        <h2>{card.name || 'Name not recorded'}</h2>
        <p className="why">{card.facility_name || 'No current building'}</p>
      </div></div>
      {said && <p className="um-notice ok" role="status" tabIndex={-1} ref={saidRef}>{said}</p>}
      <CardChips chips={chipsFor(card)} />
      <Facts card={card} />
      {!ctx.canManage && <p className="um-quiet">{READ_ONLY_WHY}</p>}
      {form === 'hand-to-umed' && <HandForm card={card} onDone={done} onCancel={cancel} />}
      {form === 'withdraw' && <WithdrawForm card={card} onDone={done} onCancel={cancel} />}
      {form === 'correction' && <CorrectionForm card={card} onDone={done} onCancel={cancel} />}
      {form === 'reason' && <ReasonForm card={card} onDone={done} onCancel={cancel} />}
      {!form && acts.length > 0 && (
        <div role="group" aria-label="Acts on this DCF resubmittal">
          {acts.map((a) => (
            <button key={a} type="button" className="um-open" style={{ marginRight: 8 }} disabled={a === 'hand-to-umed' && handAvailable(card, ctx) !== true} title={a === 'hand-to-umed' && handAvailable(card, ctx) !== true ? String(handAvailable(card, ctx)) : undefined} onClick={() => { setSaid(null); setForm(a); }}>{ACT_WORD[a]}…</button>
          ))}
        </div>
      )}
      <p style={{ marginTop: 12 }}>
        <Link to={`/portal/umed?case=${encodeURIComponent(card.case_id)}`}>Open the UMED case</Link>{' · '}
        <Link to={`/portal/finance?view=search&resident=${encodeURIComponent(card.patient_id)}`}>Open the Finance dossier</Link>
      </p>
    </section>
  );
}

/* ── New DCF resubmittal ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * What the picker searches, said when it finds nothing (Dominique, 2026-10-21: "No approved UMED matches" read as "this
 * resident has no approved UMED", when the list only held cases with a Finance handoff). The server's rule
 * (resubmittalBoard.candidates): approved or handed to Finance -- and, since Isaiah Cotto's ruling of 2026-10-29, at UMED
 * SUBMITTED TO DCF (a DCF resubmission sent again included) or SFS CORRECTION -- not closed, no DCF resubmittal or
 * corrected re-send open, the resident's current building one of the caller's -- with or without a Finance handoff.
 */
export const CANDIDATES_SEARCHED =
  'This searches residents in your buildings whose UMED is approved (in eMerge or in Monday) or handed to Finance, at UMED SUBMITTED TO DCF (a DCF resubmission sent again included), or at SFS CORRECTION; is not closed; and has no DCF resubmittal or corrected re-send open. A Finance handoff is not needed, and a UMED not yet approved has none.';

/** The empty state, naming the search when there was one. */
export function noCandidatesWords(searched: string): string {
  return searched
    ? `No UMED that can take a DCF resubmittal matches “${searched}”. ${CANDIDATES_SEARCHED}`
    : `No UMED that can take a DCF resubmittal. ${CANDIDATES_SEARCHED}`;
}

/** The status each candidate is at, in the words the UMED board uses (resubmittalState.START_STATUS_WORDS). */
export const CANDIDATE_STATUS_WORDS: Record<ResubmittalStartStatus, string> = {
  APPROVED: 'APPROVED',
  DCF_SUBMITTED: 'UMED SUBMITTED TO DCF',
  RESUB_DCF_SUBMITTED: 'UMED SUBMITTED TO DCF (a DCF resubmission sent again)',
  SFS_CORRECTION: 'SFS CORRECTION',
};

/** One candidate's line: who, where, and what is on record about the approval (or the status it is at) and Finance's handoff. */
export function candidateLine(c: ResubmittalCandidate): string {
  if (c.start_status && c.start_status !== 'APPROVED') {
    return `${c.name || 'Name not recorded'} · ${c.facility_name || 'No current building'} · ${CANDIDATE_STATUS_WORDS[c.start_status]}, not approved`;
  }
  const approved = c.approved_on
    ? ` · approved ${shortDate(c.approved_on)}`
    : (c.board_approved_on ? ` · approved ${shortDate(c.board_approved_on)} (Monday's date)` : '');
  return `${c.name || 'Name not recorded'} · ${c.facility_name || 'No current building'}${approved}`;
}

function NewForm({ onStarted }: { onStarted: (message: string) => void }) {
  const w = useWrite();
  const [q, setQ] = useState('');
  const [found, setFound] = useState<ResubmittalCandidate[] | null>(null);
  const [more, setMore] = useState(false);
  const [searched, setSearched] = useState('');
  const [findErr, setFindErr] = useState<string | null>(null);
  const [pick, setPick] = useState<ResubmittalCandidate | null>(null);
  const [requester, setRequester] = useState<ResubmittalRequester>('FACILITY');
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState<ResubmittalKind | ''>('');
  const [terms, setTerms] = useState<TermsFields>(EMPTY_TERMS);
  const [bad, setBad] = useState<string | null>(null);

  const find = useCallback(async (text: string) => {
    setFindErr(null);
    try {
      const r = await resubmittalsApi.candidates(text.trim());
      if (r.success && r.data) { setFound(r.data.items); setMore(Boolean(r.data.more)); setSearched(text.trim()); } else { setFound(null); setMore(false); setFindErr(outcomeOf(r)); }
    } catch { setFound(null); setMore(false); setFindErr('Could not reach the server.'); }
  }, []);
  useEffect(() => { find(''); }, [find]);

  const go = async () => {
    if (!pick) { setBad('Choose the UMED this resubmittal is for.'); return; }
    const p = reasonProblem(reason);
    if (p) { setBad(`A DCF resubmittal needs its reason. ${p}`); return; }
    if (!kind) { setBad('Choose whether this is a corrected or a full resubmittal.'); return; }
    const body: Parameters<typeof resubmittalsApi.start>[0] = { umed_case_id: pick.case_id, requester, reason: reason.trim(), kind };
    if (kind === 'CORRECTED') {
      const t = termsBody(terms);
      if (!t.ok) { setBad(t.message); return; }
      body.corrected_terms = t.terms;
    }
    setBad(null);
    if (await w.run(() => resubmittalsApi.start(body))) onStarted(`Started a DCF resubmittal for ${pick.name || 'this resident'}. It is With Finance until you hand it to UMED.`);
  };
  return (
    <section className="um-card" aria-label="New DCF resubmittal">
      <div className="um-card-head"><div><h2>New DCF resubmittal</h2><p className="why">Finance starts it for a UMED at APPROVED, UMED SUBMITTED TO DCF or SFS CORRECTION, whether or not Finance has its handoff yet; a UMED not yet approved has no handoff, and DCF&apos;s answer amends nothing in Finance. It stays with Finance until you hand it to UMED. Nothing is recorded until you start it.</p></div></div>
      <form role="search" aria-label="Find a UMED" onSubmit={(e) => { e.preventDefault(); find(q); }} style={{ marginBottom: 10 }}>
        <label htmlFor="rs-new-find">Find a resident or building</label>{' '}
        <input id="rs-new-find" value={q} onChange={(e) => setQ(e.target.value)} />{' '}
        <button type="submit" className="um-open">Find</button>
      </form>
      {findErr && <Alert>{findErr}</Alert>}
      {found && found.length === 0 && <p className="um-quiet" role="status">{noCandidatesWords(searched)}</p>}
      {found && found.length > 0 && (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 10px' }}>
          <legend>UMED</legend>
          {found.map((c) => (
            <label key={c.case_id} htmlFor={`rs-pick-${c.case_id}`} style={{ display: 'block', margin: '4px 0' }}>
              <input id={`rs-pick-${c.case_id}`} type="radio" name="rs-pick" checked={pick ? pick.case_id === c.case_id : false} onChange={() => setPick(c)}
                disabled={c.startable === false} />{' '}
              {candidateLine(c)}
              {c.basis && <span className="why" style={{ display: 'block', marginLeft: 22 }}>{c.basis}</span>}
              {/* Verifier D1 (2026-10-29): a fax of the case holds the start until it is recorded. */}
              {c.wait && <span className="why" role="note" style={{ display: 'block', marginLeft: 22 }}>{c.wait}</span>}
            </label>
          ))}
          {more && <p className="um-quiet" role="status">More UMEDs match than are shown. Find by the resident&apos;s name or the building to narrow the list.</p>}
        </fieldset>
      )}
      <form aria-label="Start a DCF resubmittal" onSubmit={(e) => { e.preventDefault(); go(); }}>
        <div style={{ display: 'grid', gap: 4, marginBottom: 10 }}>
          <label htmlFor="rs-new-requester">Who asked</label>
          <select id="rs-new-requester" value={requester} onChange={(e) => setRequester(e.target.value as ResubmittalRequester)}>
            {REQUESTERS.map((r) => <option key={r} value={r}>{REQUESTER_LABEL[r]}</option>)}
          </select>
        </div>
        <ReasonField id="rs-new-reason" label="Reason" value={reason} onChange={setReason} />
        <KindChoice idp="rs-new" value={kind} onChange={setKind} />
        {kind === 'CORRECTED' && <TermsInputs idp="rs-new" value={terms} onChange={setTerms} />}
        {(bad || w.err) && <Alert>{bad || w.err}</Alert>}
        <button type="submit" className="um-open um-primary" disabled={w.busy}>{w.busy ? 'Starting…' : 'Start the DCF resubmittal'}</button>
      </form>
    </section>
  );
}

/* ── The board ────────────────────────────────────────────────────────────────────────────────────────────────── */

type Columns = Array<KanbanColumnData<ResubmittalCard> & { page: number }>;

export default function ResubmittalsKanban() {
  const [st, update] = useBoardUrlState([], 'open');
  const [columns, setColumns] = useState<Columns>([]);
  const [head, setHead] = useState<ResubmittalBoardPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeFrom, setNoticeFrom] = useState<string | null>(null);
  const [noticeSeq, setNoticeSeq] = useState(0);
  const [request, setRequest] = useState<ResubmittalRequest | null>(null);
  const asks = useRef(0);
  const inFlight = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const known = useRef(new Map<string, ResubmittalCard>());
  const remember = (cards: ResubmittalCard[]) => { for (const c of cards) known.current.set(c.id, c); };

  const columnOf = (c: ResubmittalBoardPage['columns'][number]) => {
    const cards = c.cards.map(cardFromRow);
    remember(cards);
    return { key: c.key, label: c.label, what: c.what, count: c.count, cards, has_more: cards.length < c.count, page: 1, history: c.history, note: c.history ? 'History, last 30 days' : undefined };
  };

  const load = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    generation.current += 1;
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight.current = ctl;
    const stale = () => inFlight.current !== ctl;
    setLoading(true); setErr(null);
    try {
      const r = await resubmittalsApi.board({ q: st.q || undefined, limit: RESUBMITTALS_PAGE_SIZE }, ctl ? ctl.signal : undefined);
      if (stale()) return;
      if (!r.success || !r.data) { setErr(outcomeOf(r)); setColumns([]); setHead(null); return; }
      setColumns(r.data.columns.map(columnOf)); setHead(r.data);
    } catch {
      if (!stale()) { setErr('Could not reach the server, so nothing here is current.'); setColumns([]); setHead(null); }
    } finally {
      if (!stale()) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st.q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (inFlight.current) inFlight.current.abort(); }, []);

  const loadMore = useCallback(async (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col || col.loading_more) return;
    const gen = generation.current;
    setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: true } : c)));
    try {
      const r = await resubmittalsApi.board({ q: st.q || undefined, column: key, page: col.page + 1, limit: RESUBMITTALS_PAGE_SIZE });
      if (generation.current !== gen) return;
      const got = r.success && r.data ? r.data.columns[0] : null;
      setColumns((cs) => cs.map((c) => {
        if (c.key !== key) return c;
        if (!got) return { ...c, loading_more: false };
        const have = new Set(c.cards.map((k) => k.id));
        const more = got.cards.map(cardFromRow).filter((k) => !have.has(k.id));
        remember(more);
        const cards = [...c.cards, ...more];
        return { ...c, loading_more: false, count: got.count, has_more: cards.length < got.count, page: col.page + 1, cards };
      }));
      if (!got) { setNoticeFrom(null); setNotice(`The next cards could not be read. ${outcomeOf(r)}`); }
    } catch {
      if (generation.current !== gen) return;
      setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: false } : c)));
      setNoticeFrom(null); setNotice('The next cards could not be read: could not reach the server.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, st.q]);

  const ctx: ResubmittalCtx = useMemo(() => ({ canManage: Boolean(head && head.can && head.can.manage) }), [head]);

  /* A drop's request: made for one card, consumed once, void when the dock leaves that card. It records nothing. */
  const onAct = useCallback((card: ResubmittalCard, act: string) => {
    asks.current += 1;
    setRequest({ cardId: card.id, act: act as ResubmittalAct, n: asks.current });
    update({ open: card.id });
  }, [update]);
  const onConsumed = useCallback((n: number) => setRequest((r) => (r && r.n === n ? null : r)), []);
  useEffect(() => { setRequest((r) => (r && r.cardId !== st.open ? null : r)); }, [st.open]);

  const onChanged = useCallback((msg: string, from: string | null = null) => { if (msg) { setNoticeFrom(from); setNotice(msg); setNoticeSeq((n) => n + 1); } load(); }, [load]);
  const nf = new Intl.NumberFormat('en-US');
  const open = columns.filter((c) => !c.history).reduce((n, c) => n + c.count, 0);
  const creating = st.open === NEW_ID;

  return (
    <OperationalKanban<ResubmittalCard, ResubmittalCtx>
      label="DCF RESUBMIT board"
      idBase="rs-kanban"
      unit={['DCF resubmittal', 'DCF resubmittals']}
      columns={columns}
      renderCard={(card) => <ResubmittalCardBody card={card} />}
      cardName={(card) => card.name || 'DCF resubmittal'}
      transitions={RESUBMITTAL_TRANSITIONS}
      ctx={ctx}
      onAct={onAct}
      onLoadMore={loadMore}
      loading={loading}
      error={err}
      onRetry={load}
      notice={notice}
      noticeFrom={noticeFrom}
      noticeSeq={noticeSeq}
      dragDisabled={!ctx.canManage}
      emptyText={st.q ? `No DCF resubmittal here matches “${st.q}”.` : 'Nothing here.'}
      toolbar={{
        search: { value: st.q, onChange: (q) => update({ q }), placeholder: 'Resident or building', label: 'Search DCF RESUBMIT by resident or building' },
        summary: head ? <>{nf.format(open)} open {open === 1 ? 'DCF resubmittal' : 'DCF resubmittals'}{ctx.canManage ? '' : ` · ${READ_ONLY_WHY}`}</> : null,
        actions: ctx.canManage ? <button type="button" className="um-open" onClick={() => update({ open: NEW_ID })}>New resubmittal</button> : undefined,
      }}
      detail={{
        openId: st.open,
        onOpen: (id) => update({ open: id }),
        onClose: () => update({ open: null }),
        memoryKey: 'finance-resubmittals',
        label: (card) => (creating ? 'New DCF resubmittal' : card ? `${card.name || 'DCF resubmittal'}: this DCF resubmittal` : 'The open DCF resubmittal'),
        title: (card) => {
          if (creating) return <strong>New DCF resubmittal</strong>;
          const c = card || (st.open ? known.current.get(st.open) : undefined);
          return <strong>{c ? (c.name || 'DCF resubmittal') : 'DCF resubmittal'}</strong>;
        },
        render: (card) => {
          if (creating) {
            return <NewForm onStarted={(msg) => { update({ open: null }); onChanged(msg); }} />;
          }
          if (!st.open) return null;
          const c = card || known.current.get(st.open) || null;
          return <CardPanel key={st.open} id={st.open} card={c} ctx={ctx} request={request && request.cardId === st.open ? request : null} onConsumed={onConsumed} onChanged={onChanged} />;
        },
      }}
    />
  );
}

export type { ResubmittalCardRow };
